import React, { useEffect, useMemo, useState } from 'react'
import FooterButtons from '../../components/form/FooterButtons/FooterButtons'
import { Form, Spinner } from 'react-bootstrap'
import SearchBySelect from '../../components/form/SearchBySelect/SearchBySelect'
import {
  API_ERROR_MESSAGE,
  BRIDGING_PROPERTY_OPTIONS,
  INVALID_ID_WARNING,
  METROPOLITAN_PROVINCE,
  MUNICIPALITIES,
  SUBURB,
} from '../../utils/Constants'
import FormInput from '../../components/form/FormInput/FormInput'
import Disclaimer from '../../components/BridgingFinance/Disclaimer'
import ScreenLayout from '../../components/layouts/ScreenLayout'
import {
  getMetropolitansAndProvinces,
  ProvinceOrMetropolitan,
} from '../../api/common/getMetropolitansAndProvinces'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  getMunicipalitiesByProvince,
  Municipality,
} from '../../api/common/getMunicipalitiesByProvince'
import {
  getSuburbsByMunicipality,
  Suburb,
} from '../../api/common/getSuburbsByMunicipalities'
import {
  searchPropertyByErf,
  ErfResponse,
} from '../../api/bridgingFinance/searchPropertyByErf'
import {
  FullTitle,
  searchPropertyByOwner,
  SectionalTitle,
} from '../../api/bridgingFinance/searchPropertyByOwner'
import { SearchByErfNoResultsTable } from '../../components/BridgingFinance/SearchByErfNoResultsTable'
import { PropertyByOwnerNameResultsTable } from '../../components/BridgingFinance/SearchByOwnerNameResultsTable'
import { SearchBySectionalTitleNameResultsTable } from '../../components/BridgingFinance/SearchBySectionalTitleNameResultsTable'
import {
  searchPropertyBySectionalTitleName,
  SectionalTitleNameResponse,
} from '../../api/bridgingFinance/searchPropertyBySectionalTitleName'
import {
  SchemeOwnerResponse,
  searchSchemeOwnersList,
} from '../../api/bridgingFinance/searchSchemeOwnersList'
import { SchemeOwnersListResultsTable } from '../../components/BridgingFinance/SchemeOwnersListResultsTable'
import {
  SearchBySecurityEstateNameResponse,
  searchPropertyBySecurityEstateName,
} from '../../api/bridgingFinance/searchPropertyBySecurityEstateName'
import { SearchBySecurityEstateNameResultsTable } from '../../components/BridgingFinance/SearchBySecurityEstateNameResultsTable'
import {
  ComplexOwnerResponse,
  searchComplexOwnersList,
} from '../../api/bridgingFinance/searchComplexOwnersList'
import { ComplexOwnersListResultsTable } from '../../components/BridgingFinance/ComplexOwnersListResultsTable'
import {
  searchPropertyByStreetName,
  StreetNameResponse,
} from '../../api/bridgingFinance/searchPropertyByStreetName'
import { SearchByStreetNameResultsTable } from '../../components/BridgingFinance/SearchByStreetNameResultsTable'
import {
  searchStreetOwnersList,
  StreetOwnerResponse,
} from '../../api/bridgingFinance/searchStreetOwnersList'
import { StreetOwnersListResultsTable } from '../../components/BridgingFinance/StreetOwnersListResultsTable'
import {
  compareFullTitleOwners,
  compareSectionalOwners,
} from '../../utils/bridgingFinance'
import { useSelectedProperty } from '../../components/BridgingFinance/store'
import './BridgingFinance.css'
import {
  PropertyDetailsRequest,
  PropertyDetailsResponse,
  getPropertyDetails,
} from '../../api/bridgingFinance/getPropertyDetails'
import { ActiveReport } from '../../components/kyc/KycPopupComponent'
import { getRegionString } from '../../utils/getRegionString'
import { validateIdNumber } from '../../utils/Validation'
import Alert from '../../components/form/Alerts/BasicAlert'

const getPropertyDetailsRequestParams = (
  cmaid = 0,
  schemeNo = 0,
  sectionNo = 0,
) => {
  const defaultArgs: PropertyDetailsRequest = {
    cmaid: 0,
    schemeNo: 0,
    sectionNo: 0,
  }
  return cmaid
    ? {
        ...defaultArgs,
        cmaid,
      }
    : {
        ...defaultArgs,
        schemeNo: schemeNo,
        sectionNo: sectionNo,
      }
}

export type PropertyType =
  | FullTitle
  | SectionalTitle
  | SchemeOwnerResponse
  | StreetOwnerResponse
  | ComplexOwnerResponse
  | ErfResponse

interface BridgeNewSearchProps {
  setShow: (value: boolean) => void
  setActiveReport: (value: ActiveReport) => void
  setReportTypeId: (value: number) => void
  setIsError: (error: boolean) => void
  setErrorMessage: (message: string) => void
}

const BridgeNewSearch: React.FC<BridgeNewSearchProps> = ({
  setShow,
  setActiveReport,
  setIsError,
  setErrorMessage,
}) => {
  const subHeaderText =
    'Search for the property by Erf no or street Name or Sectional Title Name or Complex Name. Choose from the drop down below.'
  const [dropdownOptions, setDropdownOptions] = useState('ERF_NO')
  const onDropdownOptionsChange = (value: string) => {
    setDropdownOptions(value)
    if (value !== 'OWNER_NAME') return
    if (metroPole) fetchMunicipalities({ provinceId: metroPole.code })
    if (municipality)
      fetchSuburbs({ municipalityId: municipality.local_mun_code })
    setDropdownOptions(value)
  }
  const [metroPole, setMetropole] = useState<ProvinceOrMetropolitan | null>(
    null,
  )
  const [municipality, setMunicipality] = useState<Municipality | null>(null)
  const [suburb, setSuburb] = useState<Suburb | null>(null)
  const [hfOwnerNew, setHfOwnerNew] = useState('')
  const [hfErfNoNew, setHfErfNoNew] = useState('')
  const [hfAddressNew, sethfAddressNew] = useState('')
  const [titleSchemeNew, setTitleSchemeNew] = useState('')
  const [identityNumberNew, setIdentityNumberNew] = useState('')
  const [passportNumberNew, setPassportNumberNew] = useState('')
  const [securityEstateNew, setSecurityEstateNew] = useState('')
  const [newIdPassport, setNewIdPassport] = useState<string>('')
  const [selectedScheme, setSelectedScheme] =
    useState<SectionalTitleNameResponse | null>(null)
  const [selectedEstate, setSelectedEstate] =
    useState<SearchBySecurityEstateNameResponse | null>(null)
  const [selectedStreet, setSelectedStreet] =
    useState<StreetNameResponse | null>(null)

  const onProvinceChange = (value: string) => {
    const item = provinces?.find((item) => item.code === value)
    if (!item) return setMetropole(null)
    setMetropole(item)
    if (item.category !== 'M') fetchMunicipalities({ provinceId: item.code })
    if (item.category === 'M') fetchSuburbs({ municipalityId: item.code })
  }

  const isMetropoleSelected = useMemo(() => {
    if (!metroPole) return false
    return metroPole?.category === 'M'
  }, [metroPole])

  const onMunicipalityChange = (value: string) => {
    const item = municipalities?.find((item) => item.local_mun_code === value)
    if (!item) return
    setMunicipality(item)
    fetchSuburbs({ municipalityId: item.local_mun_code })
  }

  const onSuburbChange = (value: string) => {
    const item = suburbs?.find((item) => item.id == value)
    if (!item) return
    setSuburb(item)
  }

  const { data: provinces, isLoading: isLoadingProvinces } = useQuery<
    ProvinceOrMetropolitan[]
  >('provinces', getMetropolitansAndProvinces, {
    // onSuccess: (response) => setMetropolitans(response),
    onError: (error: any) => {
      setIsError(true)
      setErrorMessage(API_ERROR_MESSAGE)
    },
  })

  const provinceDropdownOptions = useMemo(() => {
    const defaultPlaceholder = {
      label: '- Select Metropolitan Area / Province -',
      value: '',
    }
    if (!provinces) return [defaultPlaceholder]
    const filteredProvinces = provinces.filter(
      (province: ProvinceOrMetropolitan) => province.category === 'P',
    )
    const filteredMetropolitans = provinces.filter(
      (province: ProvinceOrMetropolitan) => province.category === 'M',
    )
    return [defaultPlaceholder]
      .concat(
        filteredMetropolitans.map((province: any) => ({
          label: province.name,
          value: province.code,
        })),
      )
      .concat(
        filteredProvinces.map((province: any) => ({
          label: province.name,
          value: province.code,
        })),
      )
  }, [provinces])

  const {
    mutate: fetchMunicipalities,
    data: municipalities,
    isLoading: isLoadingMunicipalities,
  } = useMutation(['municipalities', metroPole], getMunicipalitiesByProvince, {
    onError: (error) => {
      setIsError(true)
      setErrorMessage(API_ERROR_MESSAGE)
    },
  })

  const municipalityDropdownOptions = useMemo(() => {
    const defaultPlaceholder = { label: '- Select Municipality -', value: '' }
    if (!municipalities) return [defaultPlaceholder]
    return [
      {
        label: '- Select Municipality -',
        value: '',
      },
    ].concat(
      municipalities.map((municipality: Municipality) => ({
        label: municipality.mun_name,
        value: municipality.local_mun_code,
      })),
    )
  }, [municipalities])

  const {
    mutate: fetchSuburbs,
    data: suburbs,
    isLoading: isLoadingSuburbs,
  } = useMutation(['suburbs', municipalities], getSuburbsByMunicipality, {
    onError: (error) => {
      setIsError(true)
      setErrorMessage(API_ERROR_MESSAGE)
    },
  })

  const suburbDropdownOptions = useMemo(() => {
    const defaultPlaceholder = { label: '- Select Suburb -', value: '' }
    if (!suburbs) return [defaultPlaceholder]
    return [
      {
        label: '- Select Suburb -',
        value: '',
      },
    ].concat(
      suburbs.map((suburb: Suburb) => ({
        label: suburb.text,
        value: suburb.id,
      })),
    )
  }, [suburbs])

  const {
    mutate: searchByErfNo,
    data: erfs,
    isLoading: isLoadingErfs,
  } = useMutation(['erfs', hfErfNoNew], searchPropertyByErf, {
    onError: (error: any) => {
      if (error.response && error.response.status === 500) {
        setErrorMessage(API_ERROR_MESSAGE)
        setIsError(true)
      }
    },
  })

  const {
    mutate: searchByOwner,
    data: owners,
    isLoading: isLoadingOwners,
  } = useMutation(['owners', hfOwnerNew], searchPropertyByOwner, {
    onError: (err) => {
      setIsError(true)
      setErrorMessage(API_ERROR_MESSAGE)
    },
  })

  const {
    mutate: searchBySectionalTitleName,
    data: sectionalTitleSchemes,
    isLoading: isLoadingSectionalTitleSchemes,
  } = useMutation(
    ['sectionalTitles', titleSchemeNew],
    searchPropertyBySectionalTitleName,
    {
      onError: (err) => {
        setIsError(true)
        setErrorMessage(API_ERROR_MESSAGE)
      },
    },
  )

  const {
    mutate: searchSchemeOwners,
    data: schemeOwnersList,
    isLoading: isLoadingSchemeOwnersList,
    reset: resetSchemeOnwersList,
  } = useMutation(['sectionalTitleOwners'], searchSchemeOwnersList, {
    onError: (err) => {
      setIsError(true)
      setErrorMessage(API_ERROR_MESSAGE)
    },
  })

  const {
    mutate: searchBySecurityEstateName,
    data: securityEstates,
    isLoading: isLoadingSecurityEstates,
  } = useMutation(
    ['securityEstates', securityEstateNew],
    searchPropertyBySecurityEstateName,
    {
      onError: (err) => {
        setIsError(true)
        setErrorMessage(API_ERROR_MESSAGE)
      },
    },
  )

  const {
    mutate: searchComplexOwners,
    data: complexOwners,
    isLoading: isLoadingComplexOwners,
    reset: resetComplexOwnersList,
  } = useMutation(
    ['securityEstates', selectedEstate?.complex],
    searchComplexOwnersList,
    {
      onError: (err) => {
        setIsError(true)
        setErrorMessage(API_ERROR_MESSAGE)
      },
    },
  )

  const {
    mutate: searchByStreetName,
    data: streets,
    isLoading: isLoadingStreets,
  } = useMutation(['streetNames', hfAddressNew], searchPropertyByStreetName, {
    onError: (err) => {
      setIsError(true)
      setErrorMessage(API_ERROR_MESSAGE)
    },
  })

  const {
    mutate: searchStreetOwners,
    data: streetOwners,
    isLoading: isLoadingStreetOwners,
    reset: resetStreetOwnersList,
  } = useMutation(
    ['streetNames', selectedStreet?.streetName],
    searchStreetOwnersList,
    {
      onError: (err) => {
        setIsError(true)
        setErrorMessage(API_ERROR_MESSAGE)
      },
    },
  )

  const {
    mutate: fetchProperty,
    data: property,
    isLoading: isLoadingProperty,
  } = useMutation<PropertyDetailsResponse, Error, PropertyDetailsRequest>(
    ['property'],
    getPropertyDetails,
    {
      onSuccess: (data) => {
        addProperty(data)
        handleNavToBridgingFinance()
      },
    },
  )

  const validateFields = (handleMissing: (missingFields: string[]) => void) => {
    const missingFields: string[] = []

    let isValid = true
    if (metroPole === null) {
      missingFields.push('Metropolotan Area/Province')
      isValid = false
    }

    if (dropdownOptions === 'ERF_NO' && hfErfNoNew === '') {
      missingFields.push('Erf No')
      isValid = false
    }

    if (dropdownOptions === 'OWNER_NAME' && hfOwnerNew === '') {
      const valid = hfOwnerNew !== '' && suburb !== null && municipality
      if (hfOwnerNew === '') missingFields.push('Owner Name')
      if (municipality === null) missingFields.push('Municipality')
      if (suburb === null) missingFields.push('Suburb')
      if (!valid) isValid = false
    }

    if (
      dropdownOptions === 'SECTIONAL_TITLE_SCHEME_NAME' &&
      titleSchemeNew === ''
    ) {
      missingFields.push('Sectional Title Name')
      isValid === false
    }

    if (
      dropdownOptions === 'SECURITY_ESTATE_NAME' &&
      securityEstateNew === ''
    ) {
      missingFields.push('Security Estate Name')
      isValid === false
    }

    if (dropdownOptions === 'STREET_NAME' && hfAddressNew === '') {
      missingFields.push('Street Name')
      isValid === false
    }

    if (
      dropdownOptions === 'ID_PASSPORT' &&
      (newIdPassport === '' || newIdPassport?.trim() === '')
    ) {
      isValid = validateIdNumber(newIdPassport!)
      if (!newIdPassport) missingFields.push('Id Passport')
    }

    if (handleMissing) handleMissing(missingFields)

    return isValid
  }

  const queryClient = useQueryClient()

  //handle search
  const handleSubmit = async () => {
    const isValid = validateFields((missingFields) => {
      if (missingFields.length > 0)
        alert(
          'Please fill in the following fields: ' + missingFields.join(', '),
        )
    })

    if (!isValid) return

    //check which kind of search is happening

    if (dropdownOptions === 'ERF_NO') {
      //if erf no, call erf no mutation
      const result = await searchByErfNo({
        erfNo: hfErfNoNew,
        region: getRegionString(metroPole),
      })
    }

    if (dropdownOptions === 'OWNER_NAME') {
      const region = isMetropoleSelected
        ? metroPole?.code ?? ''
        : (metroPole?.code ?? '') + ', ' + metroPole?.provinceName ?? ''
      const result = await searchByOwner({
        idNo: '',
        region: getRegionString(metroPole),
        surname: hfOwnerNew,
        suburb: suburb?.text ? suburb.text.split(', ')[0].toUpperCase() : '',
        municipalityCode: municipality?.local_mun_code ?? '',
      })
    }

    if (dropdownOptions === 'ID_PASSPORT') {
      const result = await searchByOwner({
        idNo: newIdPassport,
        region: getRegionString(metroPole),
        surname: '',
        suburb: '',
        municipalityCode: '',
      })
    }

    if (dropdownOptions === 'SECTIONAL_TITLE_SCHEME_NAME') {
      if (schemeOwnersList) resetSchemeOnwersList()
      const result = await searchBySectionalTitleName({
        sectionalTitleName: titleSchemeNew,
        region: getRegionString(metroPole),
      })
    }

    if (dropdownOptions === 'SECURITY_ESTATE_NAME') {
      if (complexOwners) resetComplexOwnersList()
      const result = await searchBySecurityEstateName({
        securityEstateName: securityEstateNew,
        region: getRegionString(metroPole),
      })
    }

    if (dropdownOptions === 'STREET_NAME') {
      if (streetOwners) resetStreetOwnersList()
      const result = await searchByStreetName({
        streetName: hfAddressNew,
        region: getRegionString(metroPole),
      })
    }
  }

  // reduce loading states for searching into one loading boolean
  const loading = useMemo(() => {
    return (
      isLoadingErfs ||
      isLoadingOwners ||
      isLoadingSectionalTitleSchemes ||
      isLoadingSecurityEstates ||
      isLoadingStreets
    )
  }, [
    isLoadingErfs,
    isLoadingOwners,
    isLoadingSectionalTitleSchemes,
    isLoadingSecurityEstates,
    isLoadingStreets,
  ])

  const { addProperty } = useSelectedProperty()

  const handleNavToBridgingFinance = () => {
    setActiveReport('BRIDGE_TWO')
  }

  const onStreetOwnerClick = async (owner: StreetOwnerResponse) => {
    await fetchProperty(getPropertyDetailsRequestParams(owner.cmaid))
  }

  const onComplexOwnerClick = async (owner: ComplexOwnerResponse) => {
    await fetchProperty(getPropertyDetailsRequestParams(owner.cmaid))
  }

  const onErfClick = async (owner: ErfResponse) => {
    await fetchProperty(getPropertyDetailsRequestParams(owner.cmaid))
  }

  const onSchemeOwnerClick = async (owner: SchemeOwnerResponse) => {
    await fetchProperty(
      getPropertyDetailsRequestParams(
        0,
        selectedScheme?.schemeNo,
        owner.sectionNo,
      ),
    )
  }

  const onSectionalOwnerClick = async (owner: SectionalTitle) => {
    await fetchProperty(
      getPropertyDetailsRequestParams(0, owner.schemeNo, owner.sectionNo),
    )
  }

  const onFullOwnerClick = async (owner: FullTitle) => {
    await fetchProperty(getPropertyDetailsRequestParams(owner.cmaid))
  }

  const onSubmitInput = (e?: React.FormEvent<HTMLInputElement>) => {
    handleSubmit()
  }

  return (
    <ScreenLayout
      headerText="BRIDGING FINANCE"
      subHeaderText={subHeaderText}
      // dividerStyle={{ marginTop: '10px' }}
    >
      <div className="search-form-container">
        {loading && (
          <>
            <div className="spinner-container">
              <Spinner
                as="span"
                animation="border"
                variant="dark"
                role="status"
                aria-hidden="true"
              ></Spinner>
              <span> </span>
            </div>
          </>
        )}
        <div className="search-form" onSubmit={(e) => handleSubmit()}>
          <SearchBySelect
            className="mb-3"
            options={BRIDGING_PROPERTY_OPTIONS}
            onChangeHandler={onDropdownOptionsChange}
          />
          <SearchBySelect
            className="mb-3"
            options={provinceDropdownOptions}
            onChangeHandler={onProvinceChange}
          />
          {dropdownOptions === 'STREET_NAME' && (
            <FormInput
              className="mb-3"
              value={hfAddressNew}
              onChangeHandler={(value) => sethfAddressNew(value)}
              placeHolderText="Type Street Name"
              onSubmit={onSubmitInput}
            />
          )}
          {dropdownOptions === 'ERF_NO' && (
            <>
              <FormInput
                className="mb-3"
                value={hfErfNoNew}
                onChangeHandler={(value) => setHfErfNoNew(value)}
                placeHolderText="Type ERF No"
                onSubmit={onSubmitInput}
              />
            </>
          )}
          {dropdownOptions === 'SECTIONAL_TITLE_SCHEME_NAME' && (
            <FormInput
              className="mb-3"
              value={titleSchemeNew}
              onChangeHandler={(value) => setTitleSchemeNew(value)}
              placeHolderText="Type Title Scheme Name."
            />
          )}
          {dropdownOptions === 'SECURITY_ESTATE_NAME' && (
            <FormInput
              className="mb-3"
              value={securityEstateNew}
              onChangeHandler={(value) => setSecurityEstateNew(value)}
              placeHolderText="Type Security Estate Name"
            />
          )}
          {dropdownOptions === 'OWNER_NAME' && (
            <>
              {!isMetropoleSelected && (
                <SearchBySelect
                  className="mb-3"
                  options={municipalityDropdownOptions}
                  onChangeHandler={onMunicipalityChange}
                />
              )}
              {isMetropoleSelected && (
                <FormInput
                  className="mb-3"
                  value={metroPole?.name ?? ''}
                  onChangeHandler={(value: string) => {}}
                  placeHolderText={metroPole?.name ?? ''}
                  disabled
                />
              )}
              <FormInput
                className="mb-3"
                value={hfOwnerNew}
                onChangeHandler={(value) => setHfOwnerNew(value)}
                placeHolderText="Type Owner's Name"
              />
              {/* <div style={{ height: '7px' }} /> */}
              <SearchBySelect
                className="mb-3"
                options={suburbDropdownOptions}
                onChangeHandler={onSuburbChange}
              />
            </>
          )}
          {dropdownOptions === 'ID_PASSPORT' && (
            <>
              <FormInput
                className="mb-3"
                value={newIdPassport}
                onChangeHandler={(value) => setNewIdPassport(value)}
                placeHolderText="Type ID Number"
              />
              {newIdPassport && !validateIdNumber(newIdPassport) && (
                <Alert variant="danger" text={INVALID_ID_WARNING} />
              )}
            </>
          )}
        </div>
        <div className="search-buttons">
          <Form.Group style={{ display: 'flex', alignItems: 'center' }}>
            <FooterButtons
              setShow={setShow}
              handleSearch={handleSubmit}
              searchChildren={'Submit'}
            />
          </Form.Group>
        </div>
      </div>
      <div style={{ width: '100%' }}>
        {dropdownOptions === 'ERF_NO' && (
          <SearchByErfNoResultsTable
            title={`Search Results: Erf ${hfErfNoNew}`}
            entries={erfs}
            onRowClick={onErfClick}
          />
        )}
        {dropdownOptions === 'OWNER_NAME' && (
          <PropertyByOwnerNameResultsTable
            title={`Search Results for owner name like: ${hfOwnerNew}`}
            entries={owners}
            onFullTitleRowClick={onFullOwnerClick}
            onSectionalTitleRowClick={onSectionalOwnerClick}
          />
        )}
        {dropdownOptions === 'ID_PASSPORT' && (
          <PropertyByOwnerNameResultsTable
            title={`Search Results for owner ID like: ${identityNumberNew}`}
            entries={owners}
            onFullTitleRowClick={onFullOwnerClick}
            onSectionalTitleRowClick={onSectionalOwnerClick}
          />
        )}
        {dropdownOptions === 'SECTIONAL_TITLE_SCHEME_NAME' &&
          schemeOwnersList && (
            <SchemeOwnersListResultsTable
              title={`Sectional Title: ${selectedScheme?.address ?? ''}`}
              entries={schemeOwnersList}
              onRowClick={onSchemeOwnerClick}
            />
          )}
        {dropdownOptions === 'SECTIONAL_TITLE_SCHEME_NAME' &&
          !schemeOwnersList && (
            <SearchBySectionalTitleNameResultsTable
              title={`Search Results for: "${titleSchemeNew}"`}
              entries={sectionalTitleSchemes}
              onRowClick={async (erf) => {
                setSelectedScheme(erf)
                searchSchemeOwners({
                  schemeNo: erf.schemeNo,
                  region: erf.region,
                })
              }}
            />
          )}
        {dropdownOptions === 'SECURITY_ESTATE_NAME' && complexOwners && (
          <ComplexOwnersListResultsTable
            title={`Estate: ${
              selectedEstate?.complex + ', ' + selectedEstate?.suburb
            }`}
            entries={complexOwners}
            onRowClick={onComplexOwnerClick}
          />
        )}
        {dropdownOptions === 'SECURITY_ESTATE_NAME' && !complexOwners && (
          <SearchBySecurityEstateNameResultsTable
            title={`Search Results for: "${securityEstateNew}"`}
            entries={securityEstates}
            onRowClick={async (complex) => {
              setSelectedEstate(complex)
              searchComplexOwners({
                complex: complex?.complex ?? '',
                region: complex.region,
                suburb: complex.suburb,
              })
            }}
          />
        )}
        {dropdownOptions === 'STREET_NAME' && streetOwners && (
          <StreetOwnersListResultsTable
            title={`Search Results for: ${selectedStreet?.streetName}`}
            entries={streetOwners}
            onRowClick={onStreetOwnerClick}
          />
        )}
        {dropdownOptions === 'STREET_NAME' && !streetOwners && (
          <SearchByStreetNameResultsTable
            title={`Search Results for: "${hfAddressNew}"`}
            entries={streets}
            onRowClick={async (street) => {
              setSelectedStreet(street)
              searchStreetOwners({
                streetName: street.streetName,
                region: street.region,
                suburb: street.suburb,
              })
            }}
          />
        )}
      </div>
      <Disclaimer />
    </ScreenLayout>
  )
}

export default BridgeNewSearch
